import { modelRegistry as Registry } from "@secretaa/contracts-platform-data";
import { IsoDateTime, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { ArtifactRef, Digest, Identity, Key, Ref, Scope, Version } from "./primitives.ts";

export const AiMode = z.enum(["UNDERSTAND", "PLAN", "RESEARCH", "PROCESS", "BUILD", "VERIFY"]);
// Runtime profile, interaction mode và model profile là ba chiều khác nhau của ADR-004.
export const AgentRuntimeProfile = z.enum(["GENERAL", "RESEARCH", "CODING", "DATA", "AUTOMATION"]);
export const ModelProfileClass=Registry.ModelProfileClass;
export const AiSession = z.object({ ...Identity, subjectId: Uuid, conversationId: Uuid, requestId: Uuid.nullable(),
  mode: AiMode, roleRef: Ref, runtimeProfile: AgentRuntimeProfile, modelProfileRef: Ref,
  state: z.enum(["OPEN", "INTERRUPTED", "CLOSED"]), revision: Version,
}).strict();
export const AiThreadBinding = z.object({ sessionId: Uuid, workspaceId: Uuid, subjectId: Uuid,
  adapter: z.literal("MASTRA"), adapterVersion: z.string().regex(/^\d+\.\d+\.\d+$/), runtimeRef: z.string().min(1).max(240),
  revision: Version, projectionCursor: z.string().max(240).nullable(), deletedAt: IsoDateTime.nullable(),
}).strict();
export const AiRole = z.object({ ...Identity, key: Key, mode: AiMode, instructionsRef: Ref, modelProfileRef: Ref,
  permittedToolRefs: z.array(Ref).max(64), dataPolicyRef: Ref, contextPolicyRef: Ref,
  executionPlane: z.enum(["DATA", "INTERNAL_DEV"]),
}).strict().refine(r => r.mode !== "BUILD" || r.executionPlane === "INTERNAL_DEV", "BUILD_REQUIRES_INTERNAL_DEV");
export const AiRun = z.object({ id: Uuid, scope: Scope, sessionRef: Ref, mode: AiMode, roleRef: Ref,
  modelProfileRef: Ref, requirementRef: Ref.nullable(), businessRunId: Uuid.nullable(),
  parentAiRunId: Uuid.nullable(), rootBudgetReservationId: Uuid, aggregatePermitId: Uuid,
  state: z.enum(["QUEUED", "RUNNING", "WAITING_FOR_USER", "INTERRUPTED", "SUCCEEDED", "FAILED"]),
  usageState: z.enum(["RESERVED", "PENDING", "SETTLED", "RECONCILING"]),
  runtime: z.object({ adapter: z.literal("MASTRA"), version: z.string().max(40), runtimeRef: z.string().max(240) }).strict().nullable(),
}).strict();
export const AiArtifact = z.object({ ...Identity, aiRunId: Uuid, kind: z.enum(["REQUIREMENT_PROPOSAL", "PLAN_PROPOSAL", "RESEARCH", "PROCESSING_RESULT", "VERIFICATION", "DEVELOPMENT_PROPOSAL"]),
  content: ArtifactRef, digest: Digest, provenanceRefs: z.array(ArtifactRef).max(128), dataPolicyRef: Ref,
}).strict().superRefine((a, ctx) => {
  if ([a.content, ...a.provenanceRefs].some(r => r.workspaceId !== a.scope.workspaceId)) ctx.addIssue({ code: "custom", message: "ARTIFACT_SCOPE_MISMATCH" });
});
export const AiHandoff = z.object({ ...Identity, fromAiRunId: Uuid, targetRoleRef: Ref, targetMode: AiMode,
  artifactRefs: z.array(ArtifactRef).min(1).max(32), contextPolicyRef: Ref, grantRef: Ref,
  rootBudgetReservationId: Uuid, aggregatePermitId: Uuid, expiresAt: IsoDateTime,
  depth: z.number().int().nonnegative().max(8), maxChildRuns: z.number().int().positive().max(16),
}).strict().refine(h => h.artifactRefs.every(a => a.workspaceId === h.scope.workspaceId), "HANDOFF_SCOPE_MISMATCH");

export const ModelProfileRegistryVersion=Registry.ModelProfileRegistryVersion;
export const ModelCatalogSnapshot=Registry.ModelCatalogSnapshot;
export const RoutingDecision = z.object({ id: Uuid, aiRunId: Uuid, scope: Scope, profileRef: Ref, catalogRef: Ref, routingPolicyRef: Ref,
  provider: Key, actualModelId: z.string().min(1).max(240), priceSnapshotRef: Ref, benchmarkResultRef: Ref,
  policyDecisionRef: Ref, requestReservationId: Uuid, aggregatePermitId: Uuid, selectedAt: IsoDateTime,
}).strict();
export type AiSession = z.infer<typeof AiSession>;
export type AiRun = z.infer<typeof AiRun>;
